<svelte:options customElement={{
	tag: "bt-lesson-sidebar",
	shadow: 'none'
}} />

<script lang="ts">
	import { onMount } from 'svelte';
	import { eventBus } from '$shared/lib/eventBus';
	import LessonSidebarCard from '$comp/lessons/sidebar/LessonSidebarCard.svelte';

	import { eyebrowForContentType } from '$comp/lessons/sidebar/LessonSidebarShared';

	import Icon from '$shared/icons/Icon.svelte';
	import ActionButton from '$shared/action-button/ActionButton.svelte';
	import ProgressBar from '$shared/progress-bar/ProgressBar.svelte';
	import { SIDEBAR_BREAKPOINT } from '$comp/lessons/sidebar/LessonSidebarConsts';

	export let visible = false;
	export let UID: string = '';
	export let associatedTopbarUID: string = '';
	export let backLinkText = '';
	export let backLinkURL = '';
	export let lessonId;
	export let contentTypeText;
	export let completeText;
	export let getURL;

	// translations :(
	export let courseModuleText = 'Class';

	let contentGroupAndPagesData: any[] = [];

	// Force string lessonId to number
	if (Number.isInteger(lessonId)) lessonId = +lessonId;

	const closeSidebar = () => {
		eventBus.emit('topbar-open', {
			targetUID: associatedTopbarUID
		});
		visible = false;
		document.querySelector('bt-lesson-sidebar').classList.add('hidden');
	}

	onMount(() => {
		const sidebar: Element | null = document.querySelector('bt-lesson-sidebar');
		if (sidebar) {
			sidebar.classList.add('hidden');
			if (window.innerWidth > SIDEBAR_BREAKPOINT) {
				visible = true;
				sidebar?.classList.remove('hidden');
			}
			eventBus.on('sidebar-open', (event) => {
				if (event.targetUID !== UID) {
					return;
				}
				visible = true;
				sidebar?.classList.remove('hidden');
				eventBus.emit('practice-close');
			});
			eventBus.on('practice-open', (event) => {
				closeSidebar();
			});
		}
		getContentGroupAndPagesData().then(data => {
			if (data.content_sections) {
				data.content_sections = data.content_sections.map((section: any) => {
					section.collapsed = false;
					return section;
				})
			}

			if (data.content_pages) {
				data.content_pages = data.content_pages.filter((contentPage => {
					return !contentPage.is_empty
				}));
			}

			contentGroupAndPagesData = data;
			// pass relevant data to paired topbar;
			eventBus.emit('topbar-initialize', {
				targetUID: associatedTopbarUID,
				contentGroupData: {
					group_duration: data.content_group.group_duration,
					group_name:  data.content_group.group_name,
					group_progress:  data.content_group.group_progress,
					group_url:  data.content_group.group_url
				}
			});
		});
		return () => {
			eventBus.off('sidebar-open');
			eventBus.off('practice-open');
		}
	});

	const getContentGroupAndPagesData = async() => {
		const response = await fetch(getURL, {
      method: 'GET',
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      credentials: 'same-origin',
    })
    if (!response.ok) {
    	console.log("Bad response:", response);
      throw new Error(`HTTP error! status: ${response.status}`)
    } else {
      const data = await response.json();
      return data;
    }
	}

	const iconNameForContentType = (raw: any) => {
		const rawString = (raw ?? '').toString().trim().toLowerCase();
		const norm = rawString.replace(/[^a-z0-9]+/g, ' '); // "Survey/Poll" -> "survey poll"

		// Native course content
		if (norm.includes('reflection')) return 'SquarePen';
		if (norm.includes('mission')) return 'Map';
		if (norm.includes('lesson') || norm.includes('course intro') || norm === 'course') return 'PlayLucide';
		if (norm.includes('discussion')) return 'MessagesSquare';
		if (norm.includes('reading')) return 'BookOpen';

		// External links
		if (norm.includes('video')) return 'Play';
		if (norm.includes('article')) return 'FileText';
		if (norm.includes('audio') || norm.includes('podcast')) return 'Headphones';
		if (norm.includes('survey') || norm.includes('poll')) return 'LayoutList';
		if (norm.includes('link')) return 'Link';

		// Fallback
		return 'Link';
	};

	const determineCardEyebrow = (contentPage) => {
		return eyebrowForContentType(contentPage, contentGroupAndPagesData.content_type_translations);
	}

	// Detect when the sidebar is showing a Curation
	$: curationLabel = (contentGroupAndPagesData?.content_type_translations?.curation || 'Curation')
		.toString()
		.toLowerCase();
	$: isCurationContext = (contentTypeText || '').toString().toLowerCase() === curationLabel;
</script>

<section class={`sidebar ${visible ? '' : 'hidden'}`}>
	<div class="sidebar__header" buttonClasses="grow-0">
		<div class="sidebar__header__buttons">
			<a href={backLinkURL}>
				<ActionButton design="text-button">
					<Icon icon="ArrowLeft" size="small"></Icon>
					{backLinkText}
				</ActionButton>
			</a>
			<ActionButton design="text-button" on:click={() => {closeSidebar()}}>
				<Icon icon="XAlt" size="medium"></Icon>
			</ActionButton>
		</div>
		<div class="sidebar__card sidebar__card--path">
			<div class="sidebar__card__contents">
				{#if contentGroupAndPagesData && contentGroupAndPagesData.content_group}
					<span class="sidebar__card__eyebrow">{contentTypeText}</span>
					<h3 class="font-semibold">{contentGroupAndPagesData.content_group.group_name}</h3>
					<span class="sidebar__card__subhead">{contentGroupAndPagesData.content_group.group_duration}</span>
				{/if}
			</div>
		</div>
		<div class="sidebar__header__progress mt-4">
			{#if contentGroupAndPagesData?.content_group?.group_progress || contentGroupAndPagesData?.content_group?.group_progress === 0}
				<ProgressBar
					color="yellow"
					numerator={contentGroupAndPagesData.content_group.group_progress}
					denominator=100
				></ProgressBar>
				<div class="sidebar__header__progress__percent-complete">{contentGroupAndPagesData.content_group.group_progress}% {completeText}</div>
			{/if}
		</div>
	</div>
	<div class="sidebar__scrollable">
		{#if contentGroupAndPagesData && contentGroupAndPagesData.content_pages}
			{#each contentGroupAndPagesData.content_pages as contentPage}
				<LessonSidebarCard
					lessonId={lessonId}
					parent={contentGroupAndPagesData.content_group}
					cardLesson={contentPage}
					cardIconName={iconNameForContentType(contentPage.type_overlay || contentPage.content_type)}
					cardEyebrow={determineCardEyebrow(contentPage)}
					translations={contentGroupAndPagesData.content_type_translations}
				/>
			{/each}
		{:else if contentGroupAndPagesData && contentGroupAndPagesData.content_sections}
			{#if contentGroupAndPagesData.pre_pages}
				{#each contentGroupAndPagesData.pre_pages as contentPage}
					<LessonSidebarCard
						lessonId={lessonId}
						parent={contentGroupAndPagesData.content_group}
						cardLesson={contentPage}
						cardIconName={iconNameForContentType(contentPage.type_overlay || contentPage.content_type)}
						cardEyebrow={determineCardEyebrow(contentPage)}
						translations={contentGroupAndPagesData.content_type_translations}
					/>
				{/each}
			{/if}
			{#if contentGroupAndPagesData.content_group?.group_intro_url}
				{@const group = contentGroupAndPagesData.content_group}
				<LessonSidebarCard
					lessonId={lessonId}
					parent={group}
					cardLesson={{
						completed: group.group_intro_completed,
						duration: group.group_intro_duration,
						id: group.group_intro_id,
						name: group.group_name,
						subtitle: "",
						url: group.group_intro_url,
						content_type: "Course Intro",
					}}
					cardIconName="Play"
					cardEyebrow={lessonId === group.group_intro_id ? 'This Course Intro' : 'Course Intro'}
					translations={contentGroupAndPagesData.content_type_translations}
				/>
			{/if}
			{#each contentGroupAndPagesData.content_sections as contentSection, index}
				<section class={`sidebar__section ${contentSection.collapsed ? 'sidebar__section--collapsed' : ''}`}>
					<ActionButton
						on:click={() => contentSection.collapsed = !contentSection.collapsed}
						design="menu-button"
						buttonClasses="sidebar-section__collapse-button"
					>
						<span class="sidebar__section__toggle__text">
							{#if isCurationContext}
								{contentSection.name}
							{:else}
								{courseModuleText} {index + 1}: {contentSection.name}
							{/if}
						</span>
						<Icon icon={contentSection.collapsed ? 'ChevronDown' : 'ChevronUp'} size="medium" iconClass="self-start" />
					</ActionButton>
					<div class="sidebar__section__contents">
						{#each contentSection.content_pages as contentPage}
							<LessonSidebarCard
								lessonId={lessonId}
								parent={contentGroupAndPagesData.content_group}
								cardLesson={contentPage}
								cardIconName={iconNameForContentType(contentPage.type_overlay || contentPage.content_type)}
								cardEyebrow={determineCardEyebrow(contentPage)}
								translations={contentGroupAndPagesData.content_type_translations}
							/>
						{/each}
					</div>
				</section>
			{/each}
			{#if contentGroupAndPagesData.post_pages}
				{#each contentGroupAndPagesData.post_pages as contentPage}
					<LessonSidebarCard
						lessonId={lessonId}
						parent={contentGroupAndPagesData.content_group}
						cardLesson={contentPage}
						cardIconName={iconNameForContentType(contentPage.type_overlay || contentPage.content_type)}
						cardEyebrow={determineCardEyebrow(contentPage)}
						translations={contentGroupAndPagesData.content_type_translations}
					/>
				{/each}
			{/if}
		{/if}
</div>
</section>

<style>
	/* Settings and base styles for container */
	.sidebar, :global(bt-lesson-sidebar) {
		--header-height: 108px;
		--footer-height: 72px;
		--sidebar-scrollable-gutter: 240px;
		position: relative;
	}
	:global(bt-lesson-sidebar:has(.sidebar.hidden)) {
		display: none;
	}
	.sidebar {
		z-index: 1;
		padding: theme('padding.6');
		padding-left: 3rem;
		padding-top: 1rem;
		min-width: 320px;
		overflow: hidden;
		height: 100%;
		&:after {
			content: '';
			display: block;
			position: absolute;
			right: 0;
			top: 1.5rem;
			height: 100%;
			width: 5px;
			background: theme('colors.neutral.200');
		}
		@media (max-width: 340px) {
			padding-left: 2rem;
			min-width: 300px;
		}
	}
	/* Sidebar header styles */
	.sidebar__header__buttons {
		display: flex;
		flex-wrap: nowrap;
		width: 100%;
		justify-content: space-between;
		align-items: center;
		margin-bottom: .5rem;
		& :nth-child(1) {
			font-size: 14px;
			font-weight: 500;
			display: flex;
			align-items: center;
			gap: 8px;
			height: 3rem;
			padding-right: 1rem;
		}
		button {
			background: white;
		}
		a {
			text-decoration: none;
		}
	}
	.sidebar__header__progress {
		border-bottom: 2px solid theme('colors.neutral.200');
	}
	.sidebar__header__progress__percent-complete {
		font-weight: 500;
		font-size: 14px;
		line-height: 20px;
		margin-bottom: 1rem;
		text-transform: lowercase;
	}

	/* Styles for the scrollable portion + the cards therein */
	.sidebar__scrollable {
		/* dynamically set - 240px based on actual space available */
		height: calc(100vh - 240px);
		width: calc(100%);
		overflow-y: scroll;
		overscroll-behavior: contain;
		scrollbar-width: none;
		padding-top: 1rem;
	}
	:global(
		.sidebar__scrollable > a.sidebar__card:last-child,
		.sidebar__scrollable > section.sidebar__section:last-child
	) {
		margin-bottom: var(--sidebar-scrollable-gutter);
	}
	.sidebar__card {
		max-width: 100%;
		padding: .5rem;
		margin-bottom: .5rem;
		background: white;
		border-radius: 8px;
		border: 1px solid transparent;
		display: grid;
		grid-template-columns: 1.5rem auto;
		gap: .5rem;
		&:first-child {
			margin-top: 1rem;
		}
	}
	.sidebar__card--path {
		padding: 0;
		.sidebar__card__contents {
			grid-column: 1 / span 2;
		}
	}
	.sidebar__card__icon {
		grid-column: 1 / span 1;
		align-self: center;
		display: grid;
		place-content: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		border: 1px solid black;
		position: relative;
	}
	.sidebar__card__contents {
		grid-column: 2 / span 1;
	}
	.sidebar__card__eyebrow {
		color: theme('colors.neutral.500');
		font-size: 12px;
		letter-spacing: 1px;
		line-height: 16px;
		text-transform: uppercase;
		display: block;
	}
	.sidebar__card h3 {
		color: black;
		font-size: 16px;
		line-height: 1.5;
		margin: 0;
		display: block;
	}
	.sidebar__card__subhead {
		color: theme('colors.neutral.500');
		font-size: 12px;
		line-height: 16px;
		font-weight: 400;
		display: block;
	}

	.sidebar__section:not(:last-child) {
		.sidebar__card:last-child {
			margin-bottom: .5rem;
		}
	}

	.sidebar__section--collapsed .sidebar__section__contents {
		display: none;
	}

	.sidebar__section__toggle__text {
		line-height: 24px;
		font-weight: 500;
		font-size: 1rem;
	}

	:global(.sidebar__section .menu-button) {
		position: sticky;
		top: -1rem;
		z-index: 1;
	}
	:global(.sidebar__section .action-button.sidebar-section__collapse-button) {
		position: initial;
		top: initial;
		z-index: 1;
		padding-right: .5rem;
		padding-left: .5rem;
	}
	:global(.sidebar__section .action-button.sidebar-section__collapse-button > .sidebar__card) {
		padding: 0;
		margin-top: 0;
		&:first-child {
			margin-top: 0;
		}
	}
</style>
